import classnames from 'classnames';

import './Pagination.css';

export interface PaginationProps {
  /**
   * Currently active page number (1-indexed)
   */
  currentPage: number;
  /**
   * Total number of pages
   */
  totalPages: number;
  /**
   * Callback when a page is selected
   */
  onPageChange: (page: number) => void;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Maximum number of page buttons to show before using ellipsis
   * @default 5
   */
  maxVisiblePages?: number;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  className,
  maxVisiblePages = 5,
  'data-testid': dataTestId,
}: PaginationProps) {
  const containerClasses = classnames('access-pagination', className);
  const baseTestId = dataTestId ?? 'pagination';

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 3) {
        endPage = Math.min(maxVisiblePages - 1, totalPages - 1);
      }

      if (currentPage >= totalPages - 2) {
        startPage = Math.max(2, totalPages - (maxVisiblePages - 2));
      }

      if (startPage > 2) {
        pages.push('...');
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      if (endPage < totalPages - 1) {
        pages.push('...');
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page: number | string) => {
    if (typeof page === 'number') {
      onPageChange(page);
    }
  };

  const pages = getPageNumbers();

  return (
    <nav
      aria-label='Pagination'
      className={containerClasses}
      data-testid={baseTestId}>
      <ul
        className='access-pagination__list'
        data-testid={`${baseTestId}-list`}>
        <li
          className='access-pagination__item'
          data-testid={`${baseTestId}-prev`}>
          <button
            aria-label='Previous page'
            className={classnames('access-pagination__button', 'access-pagination__button--prev', {
              'access-pagination__button--disabled': currentPage === 1,
            })}
            data-testid={`${baseTestId}-prev-button`}
            disabled={currentPage === 1}
            type='button'
            onClick={handlePrevious}>
            Previous
          </button>
        </li>

        {pages.map((page, index) => {
          const key = typeof page === 'number' ? page : `ellipsis-${index}`;
          const isEllipsis = page === '...';
          const isActive = page === currentPage;

          return (
            <li
              key={key}
              className='access-pagination__item'
              data-testid={isEllipsis ? `${baseTestId}-ellipsis-${index}` : `${baseTestId}-page-${String(page)}`}>
              {isEllipsis ? (
                <span
                  className='access-pagination__ellipsis'
                  data-testid={`${baseTestId}-ellipsis-text-${index}`}>
                  …
                </span>
              ) : (
                <button
                  aria-current={isActive ? 'page' : undefined}
                  aria-label={`Page ${page}`}
                  className={classnames('access-pagination__button', 'access-pagination__button--page', {
                    'access-pagination__button--active': isActive,
                  })}
                  data-testid={`${baseTestId}-page-button-${String(page)}`}
                  type='button'
                  onClick={() => handlePageClick(page)}>
                  {page}
                </button>
              )}
            </li>
          );
        })}

        <li
          className='access-pagination__item'
          data-testid={`${baseTestId}-next`}>
          <button
            aria-label='Next page'
            className={classnames('access-pagination__button', 'access-pagination__button--next', {
              'access-pagination__button--disabled': currentPage === totalPages,
            })}
            data-testid={`${baseTestId}-next-button`}
            disabled={currentPage === totalPages}
            type='button'
            onClick={handleNext}>
            Next
          </button>
        </li>
      </ul>
    </nav>
  );
}

export default Pagination;
